import type { BillRef } from './bill-ref';

/** One level of a workflow that passed a piece of work. */
export interface ClearedLevel {
  readonly level: number;
  readonly name: string;
  readonly status: string;
  readonly actionedAt: string | null;
}

/** One workflow a piece of work ran, named as the tenant designed it. */
export interface ClearedWorkflow {
  readonly workflowName: string;
  readonly levels: readonly ClearedLevel[];
}

/**
 * One row of GET /v1/vendor/billing/completed-jobs (ClaimService.completedWork):
 * a job, or an order line needing no dealer, and where its bill stands. The
 * web Completed Jobs page and the phone screen read the same row.
 */
export interface CompletedWork {
  readonly jobRequestId: string | null;
  readonly requestLineId: string | null;
  readonly jobNumber: string;
  readonly brandingTypeName: string;
  readonly brandingItemName: string;
  readonly uomCode: string;
  readonly billableQuantity: number;
  readonly verificationStatus: string;
  readonly verifiedBy: readonly ClearedWorkflow[];
  readonly billedAs: {
    readonly claimId: string;
    /** The job, dealer and date the bill is known by. */
    readonly bill: BillRef;
    readonly status: string;
    /** Still with the vendor, so it may be changed and forwarded (server's rule). */
    readonly canForward: boolean;
    /** Why Sales Accounts returned or rejected it; null otherwise. */
    readonly reason: string | null;
  } | null;
  /** Verified by every level and not yet forwarded (server's rule). */
  readonly canRaiseBill: boolean;
}

/** The work an invoice request names. */
export const workOf = (w: CompletedWork) => ({
  jobRequestId: w.jobRequestId ?? undefined,
  requestLineId: w.requestLineId ?? undefined,
});
